import { ComponentProps, ReactElement, ReactNode, useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Dialog } from '@headlessui/react';
import { AnimatePresence, motion } from 'framer-motion';
import styled from 'styled-components';

import { Button, ButtonProps } from '@components/button';
import { CancelSaveButtons, CancelSaveButtonsProps } from '@components/cancel-save-buttons';
import { useDeepEqualMemoValue } from '@components/component-helpers';
import { IconProps } from '@components/icons';
import { IconCloseRegular } from '@components/icons/close-regular';
import { RelativeSpinner } from '@components/loading-spinner';
import { HEADLESS_PORTAL_ID } from '@components/menu';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useMinimumDelay } from '@hooks/use-minimum-delay';
import { useTranslate } from '@locale/translate';
import { createCountStore } from '@stores/create-count-store';
import { Color, theme } from '@styles/theme';
import { noop } from '@utils/browser';
import { pxToRem } from '@utils/converters';
import { second } from '@utils/milliseconds';
import { MakePropertiesOptional, Transient } from '@utils/types';

export type ModalOptions = {
  variant?: ModalVariant;
  backgroundColor?: Color | 'gradient';
  hasBlurredAndForcedBackdrop?: boolean;
  onHide?(): void;
  onBeforeChange?(): boolean | void;
};

export const modalStore = createCountStore();

const handleHide = (forceHide: boolean) => {
  if (confirmationRegistry) {
    confirmationRegistry.options?.onHide?.();
    confirmationRegistry = null;
    confirmationModalStore.reset();
    if (!forceHide) {
      return;
    }
  }
  if (modalRegistry?.options) {
    const { onBeforeChange, onHide } = modalRegistry.options;
    const shouldContinue = forceHide || (onBeforeChange?.() ?? true);
    if (shouldContinue) {
      onHide?.();
      modalRegistry = null;
      modalStore.reset();
    }
  }
};

export const hideModal = () => handleHide(false);

export const forceHideModal = () => handleHide(true);

type ModalComponent = (props: any) => ReactElement;

type ModalRegistry = {
  options?: ModalOptions;
  ModalComponent(props: object): ReactElement;
};

let modalRegistry: ModalRegistry | null = null;
let modalInstanceId = 0;

const confirmationModalStore = createCountStore();

let confirmationRegistry: ModalRegistry | null = null;

export type ModalVariant = 'REGULAR' | 'LARGE' | 'EXTRA_LARGE' | 'EXTRA_EXTRA_LARGE' | 'FULLSCREEN';

type ModalSizeProps = {
  width: string;
  maxWidth?: string;
  maxHeight: string;
  height: string;
};

type ModalSizes = {
  [key in ModalVariant]: ModalSizeProps;
};

const sizes: ModalSizes = {
  REGULAR: {
    width: pxToRem(504),
    maxHeight: '90vh',
    height: 'inherit',
  },
  LARGE: {
    width: pxToRem(728),
    maxHeight: '90vh',
    height: 'inherit',
  },
  EXTRA_LARGE: {
    width: '88vw',
    maxWidth: pxToRem(960),
    maxHeight: '72vh',
    height: '72vh',
  },
  EXTRA_EXTRA_LARGE: {
    width: pxToRem(1200),
    maxWidth: pxToRem(1200),
    maxHeight: pxToRem(568),
    height: pxToRem(568),
  },
  FULLSCREEN: {
    width: '94vw',
    maxHeight: '90vh',
    height: '90vh',
  },
};

const TRANSITION_DURATION = 0.3;
const BACKDROP_OPACITY = 0.5;

export const useModal = <T extends ModalComponent>(component: T, options?: ModalOptions) => {
  const opts = useDeepEqualMemoValue(options);
  return useCallback(
    <K extends ComponentProps<T>>(props?: K, optionsOnShow?: ModalOptions) => {
      return showModal({
        component,
        options: { ...opts, ...optionsOnShow },
        props,
      });
    },
    [component, opts]
  );
};

export const showModal = <T extends ModalComponent, K extends ComponentProps<T>>({
  component: ModalComponent,
  options,
  props,
}: {
  component: T;
  options?: ModalOptions;
  props?: K;
}) => {
  modalInstanceId++;
  modalRegistry = { options: { ...options, ...props }, ModalComponent };
  modalStore.increase();
};

export const useIsModalActive = () => {
  const { useStore } = modalStore;
  const isActive = useStore((s) => s.count > 0);

  return isActive;
};

const showConfirmationModal = <T extends ModalComponent, K extends ComponentProps<T>>({
  component: ModalComponent,
  options,
  props,
}: {
  component: T;
  options?: ModalOptions;
  props?: K;
}) => {
  confirmationRegistry = { options: { ...options, ...props }, ModalComponent };
  confirmationModalStore.increase();
};

export const useConfirmationModal = <T extends ModalComponent>(
  component: T,
  options?: ModalOptions
) => {
  const opts = useDeepEqualMemoValue(options);
  return useCallback(
    <K extends ComponentProps<T>>(props?: K) => {
      showConfirmationModal({ component, options: opts, props });
    },
    [component, opts]
  );
};

export const useModalEventHandlers = () => {
  const setOnBeforeChange = (onBeforeChange: ModalOptions['onBeforeChange']) => {
    if (modalRegistry) {
      modalRegistry.options = { ...modalRegistry.options, onBeforeChange };
    }
  };

  return { setOnBeforeChange };
};

type ModalProps = React.HTMLAttributes<HTMLDivElement> & {
  isLoading?: boolean;
  children?: ReactNode;
};

const MIN_LOADING_DISPLAY_MS = 500;

export const Modal = ({ isLoading = false, children, ...props }: ModalProps) => {
  const showLoading = useMinimumDelay(isLoading, MIN_LOADING_DISPLAY_MS);

  return (
    <ModalWrapper {...props}>
      {children}
      <AnimatePresence>
        {showLoading && (
          <LoadingOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            <RelativeSpinner />
          </LoadingOverlay>
        )}
      </AnimatePresence>
    </ModalWrapper>
  );
};

const ModalWrapper = styled.div({
  display: 'contents',
});

const LoadingOverlay = styled(motion.div)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: theme.color.backgroundLightDark,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: pxToRem(16),
  zIndex: 10,
});

const ModalProvider = () => {
  const { useStore } = modalStore;
  const count = useStore((s) => s.count);
  const isActive = count > 0;
  // isDialogOpen is used to allow the hide animation to complete before the dialog is closed
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const location = useLocation();
  const { options, ModalComponent } = modalRegistry ?? {};
  const {
    onHide,
    variant = 'REGULAR',
    backgroundColor,
    hasBlurredAndForcedBackdrop,
  } = options ?? {};

  useEffect(() => {
    forceHideModal();
  }, [location.pathname]);

  useEffect(() => {
    if (isActive) {
      setIsDialogOpen(true);
    } else {
      const timer = setTimeout(() => setIsDialogOpen(false), TRANSITION_DURATION * second);
      return () => clearTimeout(timer);
    }
  }, [isActive]);

  useEffect(() => {
    if (!isActive && !isDialogOpen) {
      onHide?.();
      modalRegistry = null;
    }
    // eslint-disable-next-line
  }, [isActive, isDialogOpen]);

  return (
    <Dialog
      id={HEADLESS_PORTAL_ID}
      as="div"
      onClose={hasBlurredAndForcedBackdrop ? noop : hideModal}
      open={isDialogOpen}
    >
      <StyledContainer>
        <AnimatePresence>
          {isActive && (
            <>
              <Backdrop
                initial={{ opacity: 0 }}
                animate={{ opacity: hasBlurredAndForcedBackdrop ? 1 : BACKDROP_OPACITY }}
                transition={{ duration: TRANSITION_DURATION }}
                exit={{ opacity: 0 }}
                $hasBlurredAndForcedBackdrop={hasBlurredAndForcedBackdrop}
              />
              <Dialog.Panel>
                <Container
                  key={modalInstanceId}
                  className={backgroundColor === 'gradient' ? 'dark' : undefined}
                  $variant={variant}
                  $backgroundColor={backgroundColor}
                  id="modal"
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: TRANSITION_DURATION, ease: 'easeOut' }}
                >
                  {ModalComponent && <ModalComponent {...options} />}
                </Container>
              </Dialog.Panel>
            </>
          )}
        </AnimatePresence>
        <ConfirmationLayer />
      </StyledContainer>
    </Dialog>
  );
};

// Its own nested Dialog, not a second Dialog.Panel — else headlessui reads a click on the confirmation as an outside-click on the host modal and re-opens it.
const ConfirmationLayer = () => {
  const isActive = confirmationModalStore.useStore((state) => state.count > 0);
  const { options, ModalComponent } = confirmationRegistry ?? {};

  return (
    <Dialog as="div" onClose={hideModal} open={isActive}>
      {isActive && ModalComponent && (
        <ConfirmationContainer data-testid="confirmation-modal">
          <Backdrop
            initial={{ opacity: 0 }}
            animate={{ opacity: BACKDROP_OPACITY }}
            transition={{ duration: TRANSITION_DURATION }}
            exit={{ opacity: 0 }}
          />
          <Dialog.Panel>
            <Container
              $variant="REGULAR"
              $backgroundColor={undefined}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: TRANSITION_DURATION, ease: 'easeOut' }}
            >
              <ModalComponent {...options} />
            </Container>
          </Dialog.Panel>
        </ConfirmationContainer>
      )}
    </Dialog>
  );
};

const ConfirmationContainer = styled.div({
  position: 'fixed',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000000, // above a host modal's own full-screen overlays (e.g. the code editor's loader at 999999)
});

const StyledContainer = styled(motion.div)({
  position: 'fixed',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 9999,
});

const Backdrop = styled(motion.div)<Transient<ModalOptions, 'hasBlurredAndForcedBackdrop'>>(
  ({ $hasBlurredAndForcedBackdrop }) => ({
    position: 'fixed',
    backgroundColor: theme.color.backdropOverlay,
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    overflow: 'hidden',
    backdropFilter: $hasBlurredAndForcedBackdrop ? `blur(${pxToRem(8)})` : undefined,
    zIndex: -1,
  })
);

const PADDING = pxToRem(40);

const Container = styled(motion.div)<{
  $variant: ModalOptions['variant'];
  $backgroundColor: ModalOptions['backgroundColor'];
}>(({ $variant = 'REGULAR', $backgroundColor }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column' as 'column',
  overflow: $variant === 'FULLSCREEN' ? 'auto' : 'hidden',
  width: sizes[$variant].width,
  maxWidth: sizes[$variant].maxWidth,
  height: sizes[$variant].height,
  maxHeight: sizes[$variant].maxHeight,
  ...($backgroundColor === 'gradient'
    ? {
        backgroundColor: theme.color.purple400,
        backgroundImage: `radial-gradient(ellipse 120% 100% at 100% 50%, ${theme.color.violet100} -64%, ${theme.color.tealTrans10} 70%)`,
      }
    : {
        backgroundColor: $backgroundColor
          ? theme.color[$backgroundColor]
          : theme.color.backgroundLightDark,
      }),
  borderRadius: pxToRem(16),
  boxShadow: theme.shadow.fancy,
  ...($variant !== 'FULLSCREEN' && {
    padding: `${pxToRem(24)} 0 ${PADDING} 0`,
  }),
}));

export const MODAL_CONTENT_CLASSNAME = 'modal-content';

const ModalContent = styled.div.attrs({
  className: MODAL_CONTENT_CLASSNAME,
})({
  overflow: 'auto',
  padding: `0 ${PADDING}`,
  minHeight: 0,
  flexGrow: 1,
  font: theme.font.regular16,
  color: theme.color.textPrimary,
});

export const MODAL_HEADER_CLASSNAME = 'modal-header';

const ModalHeader: React.FC = styled(Dialog.Title).attrs({ as: 'div' })({
  padding: `${pxToRem(4)} ${PADDING}`,
  marginBottom: pxToRem(16),
  font: theme.font.heading1,
  color: theme.color.textPrimary,
});

export const MODAL_FOOTER_CLASSNAME = 'modal-footer';

const ModalFooter = styled.div.attrs({
  className: MODAL_FOOTER_CLASSNAME,
})({
  marginTop: pxToRem(24),
  padding: `0 ${PADDING}`,
});

export type SimpleHeaderProps = {
  title: string;
  color?: Color;
  icon?: ReactElement<IconProps>;
  skipCloseButton?: boolean;
  closeButtonProps?: ButtonProps;
};

const SimpleHeader = ({
  title,
  icon,
  skipCloseButton = false,
  closeButtonProps,
  ...rest
}: SimpleHeaderProps) => (
  <StyledTitleHeader {...rest}>
    {icon ? <SimpleIconHeader icon={icon} title={title} /> : <HeaderTitle title={title} />}
    {!skipCloseButton && <CloseButton {...closeButtonProps} />}
  </StyledTitleHeader>
);

const StyledTitleHeader = styled(ModalHeader)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

export const CloseButton = (props: ButtonProps) => (
  <Button iconLeft={<IconCloseRegular />} variant="ghost" onClick={hideModal} {...props} />
);

const HeaderTitle = ({ title, color }: Pick<SimpleHeaderProps, 'title' | 'color'>) => (
  <Text color={color} text={title} font="heading1" />
);

const SimpleIconHeader = ({
  title,
  color,
  icon,
}: Pick<SimpleHeaderProps, 'title' | 'color' | 'icon'>) => (
  <Stack space={2} orientation="horizontal" align="center">
    {icon}
    <HeaderTitle color={color} title={title} />
  </Stack>
);

type SimpleFooterProps = MakePropertiesOptional<
  Omit<CancelSaveButtonsProps, 'alwaysShow'>,
  'onCancel'
>;

const SimpleFooter = ({ onCancel, onSuccess, ...rest }: SimpleFooterProps) => (
  <ModalFooter>
    <CancelSaveButtons
      {...rest}
      alwaysShow
      onCancel={() => {
        onCancel?.();
        hideModal();
      }}
      onSuccess={() => {
        onSuccess?.();
        hideModal();
      }}
    />
  </ModalFooter>
);

const TruncatedTitle = styled(Stack)({
  minWidth: 0,
  overflow: 'hidden',
  '& > *': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

const FullscreenHeader = ({ title, actions }: { title: string; actions?: JSX.Element }) => {
  const { t } = useTranslate();
  return (
    <StyledFullscreenHeader>
      <TruncatedTitle orientation="horizontal" align="center">
        <Text font="bold16" text={title} />
      </TruncatedTitle>
      <Stack orientation="horizontal" align="center" space={6}>
        <HeaderActions id="header-actions">{actions}</HeaderActions>
        <Button
          aria-label={t('core.close')}
          iconLeft={<IconCloseRegular />}
          variant="secondary"
          onClick={hideModal}
        />
      </Stack>
    </StyledFullscreenHeader>
  );
};

const StyledFullscreenHeader = styled(ModalHeader)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  minHeight: pxToRem(88),
  backgroundColor: theme.color.backgroundLightDark,
  marginBottom: 0,
});

export const HeaderActions = styled.div({
  flexGrow: 1,
  display: 'flex',
  justifyContent: 'flex-end',
  position: 'relative',
});

const FullscreenContent = styled.div({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexGrow: 1,
});

const FullscreenFooter = styled.div.attrs({
  className: MODAL_FOOTER_CLASSNAME,
})({
  margin: `${pxToRem(24)} 0`,
  padding: `0 ${PADDING}`,
});

Modal.Provider = ModalProvider;
Modal.SimpleHeader = SimpleHeader;
Modal.SimpleFooter = SimpleFooter;
Modal.HeaderTitle = HeaderTitle;
Modal.Header = ModalHeader;
Modal.Content = ModalContent;
Modal.Footer = ModalFooter;
Modal.FullscreenHeader = FullscreenHeader;
Modal.FullscreenContent = FullscreenContent;
Modal.FullscreenFooter = FullscreenFooter;
